import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Check, ChevronRight, Plus, Minus, Phone, ArrowUpRight, HeartPulse } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow, KineticLines, Parallax, GoldButton, OutlineButton, EASE } from "@/lib/ui";
import { getService, getServices, SP_LABELS } from "@/data/services";
import { getSite } from "@/data/site";
import { useLang } from "@/i18n/lang";

export default function ServicePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const L = SP_LABELS[lang];
  const { CONTACT } = getSite(lang);
  const service = getService(lang, slug);
  const [open, setOpen] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  if (!service) return <Navigate to="/services" replace />;

  const related = getServices(lang).filter((s) => s.slug !== slug).slice(0, 3);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "MedicalProcedure", name: service.name, description: service.seoDesc,
    procedureType: "https://schema.org/NoninvasiveProcedure",
    provider: { "@type": "Dentist", name: "Glamour Dent", telephone: "+40-720-555-019" },
  };

  return (
    <>
      <Seo title={service.seoTitle} description={service.seoDesc} path={`/services/${slug}`} jsonLd={jsonLd} />

      <section ref={heroRef} className="relative h-[80vh] min-h-[600px] overflow-hidden bg-ink grain" data-testid="service-hero">
        <motion.div style={{ y: imgY, scale }} className="absolute inset-0">
          <img src={service.hero} alt={service.name} className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        </motion.div>
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
          <nav className="flex items-center gap-2 text-white/50 font-accent text-xs tracking-wide mb-8" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-gold transition-colors">{L.home}</Link><ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-gold transition-colors">{L.services}</Link><ChevronRight className="w-3 h-3" />
            <span className="text-white/80">{service.name}</span>
          </nav>
          <Eyebrow dark>{L.treatment}</Eyebrow>
          <h1 className="font-serif font-light text-white text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl"><KineticLines lines={[service.name]} delay={0.3} /></h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-6 text-white/65 text-xl font-light font-serif italic max-w-2xl">{service.tagline}</motion.p>
        </div>
      </section>

      <section className="bg-white text-ink py-28 md:py-36" data-testid="service-description">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
          <div>
            <Eyebrow>{L.overview}</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight mb-8">{L.theTreatment}</h2>
            <p className="text-ink/60 text-lg font-light leading-relaxed">{service.description}</p>
          </div>
          <div className="rounded-3xl bg-ink text-white p-10 h-full">
            <h3 className="font-serif text-3xl mb-8">{L.benefits}</h3>
            <ul className="space-y-5">{service.benefits.map((b) => (<li key={b} className="flex gap-4 items-start"><span className="w-6 h-6 rounded-full bg-gold flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-ink" /></span><span className="text-white/75 font-light">{b}</span></li>))}</ul>
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-white py-28 md:py-36 grain relative" data-testid="service-procedure">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16"><Eyebrow dark>{L.procedureEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">{L.procedureTitle}<span className="gold-text italic">{L.procedureGold}</span></h2></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.procedure.map((p, i) => (
              <div key={p.t} className="rounded-3xl border border-white/10 p-8 h-full hover:bg-ink transition-colors duration-500 group">
                <span className="font-serif text-5xl text-white/10 group-hover:text-gold/40 transition-colors">0{i + 1}</span>
                <h3 className="font-serif text-2xl mt-4 mb-3">{p.t}</h3><p className="text-white/55 font-light text-sm leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="bg-white text-ink py-32 md:py-40"
        data-testid="service-recovery"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          <div className="grid lg:grid-cols-[420px_1fr] gap-20 items-start">

            <div className="sticky top-32">

              <Eyebrow>{L.recoveryEye}</Eyebrow>

              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-tight flex items-center gap-3">
                <HeartPulse className="w-7 h-7 text-gold shrink-0" />
                {L.recoveryTitle}
              </h2>

              <p className="mt-8 text-lg leading-8 text-ink/60 font-light">
                {service.recovery}
              </p>

            </div>

            <Parallax offset={40}>

              <div className="grid grid-cols-2 gap-6">

                {service.gallery.map((src, i) => (

                  <div
                    key={i}
                    className={`
                      group
                      overflow-hidden
                      rounded-[30px]
                      shadow-sm
                      hover:shadow-xl
                      transition-all
                      duration-500

                      ${
                        i === 0
                          ? "col-span-2 aspect-[16/9]"
                          : "aspect-square"
                      }
                    `}
                  >

                    <img
                      src={src}
                      alt={`${service.name} ${i + 1}`}
                      className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                    />

                  </div>

                ))}

              </div>

            </Parallax>

          </div>

        </div>
      </section>

      {service.videos?.length ? (
        <section className="bg-ink text-white py-28 md:py-36 grain relative" data-testid="service-videos">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-14"><Eyebrow dark>{L.videoEye}</Eyebrow><h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight">{L.videoTitle}</h2></div>
            <div className="flex flex-wrap gap-8 justify-center lg:justify-start">
              {service.videos.map((v, i) => (
                <div key={i} className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-ink" style={{ maxWidth: 300, width: "100%" }}>
                  <iframe title={`video-${i}`} src={v} width="300" height="533" style={{ border: "none", overflow: "hidden", width: "100%", display: "block" }} scrolling="no" frameBorder="0" allowFullScreen allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="bg-ink text-white py-28 md:py-36 grain relative" data-testid="service-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16"><h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight">{L.faqTitle}<span className="gold-text italic">{L.faqGold}</span></h2></div>
          <div className="divide-y divide-white/10 border-t border-white/10">
            {service.faq.map((f, i) => (
              <div key={i} className="py-6" data-testid={`service-faq-${i}`}>
                <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-6 text-left group">
                  <span className="font-serif text-xl md:text-2xl group-hover:text-gold transition-colors">{f.q}</span>
                  <span className="shrink-0 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center group-hover:border-gold transition-colors">{open === i ? <Minus className="w-4 h-4 text-gold" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden"><p className="pt-5 text-white/55 font-light leading-relaxed max-w-2xl">{f.a}</p></motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gold text-ink py-28 md:py-36 text-center relative overflow-hidden" data-testid="service-cta">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-tight">{L.ctaPre} {service.name}?</h2>
          <p className="mt-6 text-ink/70 font-light text-lg">{L.ctaP}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={CONTACT.phoneHref} data-testid="service-cta-call"><motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 font-accent text-sm text-white"><Phone className="w-4 h-4" /> {L.call} {CONTACT.phone}</motion.button></a>
            <Link to="/services"><OutlineButton>{L.allServices} <ArrowUpRight className="w-4 h-4" /></OutlineButton></Link>
          </div>
        </div>
      </section>

      <section className="bg-white text-ink py-24" data-testid="service-related">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow>{L.explore}</Eyebrow>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-${s.slug}`} className="group block rounded-3xl overflow-hidden border border-ink/8 relative h-56">
                <img src={s.hero} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white"><h3 className="font-serif text-2xl">{s.name}</h3><span className="inline-flex items-center gap-1.5 text-gold font-accent text-xs tracking-wide mt-1">{L.discover} <ArrowUpRight className="w-3.5 h-3.5" /></span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
